import { ReactNode } from 'react'
import { css, Theme, useTheme } from '@emotion/react'

const gridStyle = css`
  display: flex;
  flex-direction: column;
`

const itemStyle = css`
  display: flex;
  flex-direction: row;
  margin-top: 8px;
  align-items: center;

  &:first-of-type {
    margin-top: 0;
  }
`

const labelStyle = css`
  font-family: 'Noto Sans', sans-serif;
  font-size: 1.4rem;
  font-weight: 400;
  line-height: 2.2rem;
  color: #767676;
  width: 200px;
  flex-shrink: 0;
`

const valueStyle = (t: Theme) => css`
  font-size: 1.4rem;
  font-weight: 400;
  line-height: 2.2rem;
  color: ${t.c.bc.text};
  min-height: 2.2rem;
`

const valueWrapperStyle = css`
  display: flex;
  align-items: center;
  gap: 20px;
  flex: 1;
  min-width: 0;
`

const dotIndicatorStyle = (color: string) => css`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${color};
`

const textIndicatorStyle = (t: Theme) => css`
  font-size: 1.4rem;
  font-weight: 400;
  line-height: 2.2rem;
  color: ${t.c.ec.darkGray};
  flex-shrink: 0;
`

const indicatorBlockStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 1px;
  flex-shrink: 0;
`

export type FieldGridProps = {
  children: ReactNode
}

export function FieldGrid({ children }: FieldGridProps) {
  return <div css={gridStyle}>{children}</div>
}

export type FieldItemIndicator = { type: 'dotAndText'; color: string; text: string }

export type FieldItemProps = {
  label: string
  value: ReactNode
  indicator?: FieldItemIndicator
}

export function FieldItem({ label, value, indicator }: FieldItemProps) {
  const theme = useTheme()
  const displayIndicator = indicator && renderIndicator(indicator)
  return (
    <div css={itemStyle}>
      <span css={labelStyle}>{label}</span>
      <span css={valueWrapperStyle}>
        <span css={valueStyle(theme)}>{value}</span>
        {displayIndicator}
      </span>
    </div>
  )
}

function renderIndicator(indicator: FieldItemIndicator): ReactNode {
  const color = indicator.color
  return (
    <span css={indicatorBlockStyle}>
      <span css={dotIndicatorStyle(color)} aria-hidden />
      <span css={textIndicatorStyle}>{indicator.text}</span>
    </span>
  )
}
